// src/pages/LitActSection.jsx
//
// Student-facing render of an ACT lit_section (sectionKind 'act'). Faithful port of the
// prototype LitActView (between-the-lines-v2.html L12468-12537): a 3-tab view --
// Scene Breakdown / Key Quotes / Significance -- using the prototype's real markup/classes.
//
// Reads the flat ACT_BLOCKS (litSectionShape.js, mirrors LitActEditor): blocks.scenes
// ([{n,title,plot,stagecraft}]), blocks.keyQuotes ([{quote,speaker,ref,analysis}]),
// blocks.significance ([{title,text}]). The section title (content.title) is the act label.
//
// Pure presentational (takes `content` directly). Deferred like the Overview port:
// glossary highlighting (renderLitText -> plain text), Export PDF, and the podcast player.
import React, { useState } from 'react';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const TABS = [
    ['scenes', 'Scene Breakdown'],
    ['quotes', 'Key Quotes'],
    ['significance', 'Significance'],
];

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function LitActSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const scenes = Array.isArray(blocks.scenes) ? blocks.scenes : [];
    const keyQuotes = Array.isArray(blocks.keyQuotes) ? blocks.keyQuotes : [];
    const significance = Array.isArray(blocks.significance) ? blocks.significance : [];
    const [tab, setTab] = useState('scenes');

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow={(content && content.title) || 'Act'} title="Scenes & stagecraft"
                blocks={blocks} done={done} />

            <div className="inline-flex border border-book-accent/40 mb-6 rounded-sm overflow-hidden flex-wrap">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${tab === k ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === 'scenes' && (
                scenes.length > 0 ? (
                    <div className="space-y-4">
                        {scenes.map((s, i) => (
                            <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                                    <div>
                                        <div className="font-display text-book-accent text-xs font-semibold">{s && s.n}</div>
                                        <div className="font-display text-book-text text-base">{s && s.title}</div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="font-display text-book-muted text-xs mb-1 font-semibold">Plot</div>
                                    <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap">{s && s.plot}</p>
                                </div>
                                <div>
                                    <div className="font-display text-book-muted text-xs mb-1 font-semibold">Stagecraft</div>
                                    <p className="font-serif italic text-book-text/90 leading-relaxed whitespace-pre-wrap">{s && s.stagecraft}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'quotes' && (
                keyQuotes.length > 0 ? (
                    <div className="space-y-3">
                        {keyQuotes.map((q, i) => (
                            <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <span className="font-display text-book-accent text-xs font-semibold">{q && q.ref}</span>
                                    {q && q.speaker && <span className="font-serif italic text-book-muted text-xs">· {q.speaker}</span>}
                                </div>
                                <p className="font-serif italic text-book-text text-base leading-relaxed mb-2">"{q && q.quote}"</p>
                                <p className="font-serif text-book-text/80 text-sm leading-relaxed">→ {q && q.analysis}</p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'significance' && (
                significance.length > 0 ? (
                    <div className="space-y-3">
                        <p className="font-serif italic text-book-muted mb-2">Why this act matters for the whole play.</p>
                        {significance.map((s, i) => (
                            <div key={i} className="border border-book-accent/30 bg-book-accent/5 p-5 swp-corner text-book-accent">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">{s && s.title}</div>
                                <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap">{s && s.text}</p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}
        </div>
    );
}
